import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { downloadFile, hasOfflineCopy, isOffline } from "@/lib/api";
import {
  PENDING_EVENT, discardPendingHarvest, isNetworkError, newClientRef, pendingHarvests, queueHarvest,
} from "@/lib/offline";
import { PageHeader } from "@/components/PageHeader";
import { fmtMoney, todayStr, amountInWords } from "@/lib/format";
import { useTenant } from "@/lib/tenant";
import { PrintableStatement } from "@/components/PrintableStatement";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash, Money, Plant, CaretRight, CaretLeft, Handshake, FileText, X, WhatsappLogo, ArrowCircleDown, ArrowCircleUp, MagnifyingGlass, Coins, PencilSimple, ArrowCounterClockwise, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_BAG_TARE_KG = "0.75"; // starting value for the editable per-lot bag-tare field
const FALLBACK_BAG_BATCH = 4; // used only if the tenant setting hasn't loaded yet

// What the ×N toggle cycles a reading through, for the last odd bag(s) --
// the batch size itself, a halfway point (for an odd leftover pair), then 1.
function bagBatchCycle(batch) {
  const cycle = [batch, Math.max(1, Math.floor(batch / 2)), 1];
  return cycle.filter((v, i) => cycle.indexOf(v) === i);
}

const openWa = (text, phone) => {
  let clean = (phone || "").replace(/\D/g, "");
  if (clean.length === 10) clean = "91" + clean;
  const base = clean ? `https://wa.me/${clean}` : "https://wa.me/";
  window.open(`${base}?text=${encodeURIComponent(text)}`, "_blank");
};

const netLabel = (net) => {
  if (net > 0) return { text: `We owe ${fmtMoney(net)}`, cls: "text-emerald-700" };
  if (net < 0) return { text: `Owes us ${fmtMoney(-net)}`, cls: "text-rose-700" };
  return { text: "Settled", cls: "text-slate-500" };
};

// Saves a copy of every farmer's detail that isn't on this device yet, one
// at a time in the background, so any farmer can be opened at a farm with
// no network -- not just the ones opened recently. Already-saved farmers
// are refreshed whenever they're opened.
let warming = false;
async function saveFarmersForOffline(farmers) {
  if (warming || isOffline()) return;
  warming = true;
  try {
    for (const f of farmers) {
      const url = `/farmers/${f.id}`;
      if (hasOfflineCopy(url)) continue;
      await api.get(url, { params: { as_of: todayStr() } });
    }
  } catch {
    // network dropped -- carry on next time the list loads
  } finally {
    warming = false;
  }
}

const OVERVIEW_TONES = {
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
  slate: "bg-slate-100 text-slate-600",
};

const OverviewMetric = ({ label, value, icon: Icon, tone, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">{label}</span>
      <div className={`h-7 w-7 rounded-md flex items-center justify-center ${OVERVIEW_TONES[tone]}`}><Icon size={15} weight="bold" /></div>
    </div>
    <div className="font-data text-xl md:text-2xl font-semibold tracking-tight text-slate-900">{value}</div>
  </div>
);

function FarmersOverview({ farmerCount, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/arecanut/dashboard").then((r) => setData(r.data)); }, [refreshKey]);
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <OverviewMetric testid="areca-advance" label="Total Advances" value={fmtMoney(data.total_advance)} icon={Money} tone="amber" />
        <OverviewMetric testid="areca-interest" label="Accrued Interest" value={fmtMoney(data.total_interest)} icon={Money} tone="slate" />
        <OverviewMetric testid="areca-owed-us" label="Farmers Owe Us" value={fmtMoney(data.farmers_owe_us)} icon={ArrowCircleDown} tone="rose" />
        <OverviewMetric testid="areca-we-owe" label="We Owe Farmers" value={fmtMoney(data.we_owe_farmers)} icon={ArrowCircleUp} tone="emerald" />
      </div>

      <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-900 text-white p-6">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/37636717/pexels-photo-37636717.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} />
        <div className="relative">
          <h2 className="text-base font-medium mb-1">Arecanut Advance Ledger</h2>
          <p className="text-sm text-slate-300 max-w-lg">Record cash advances against a farmer's crop, apply simple interest (annual %), and settle at harvest with quintals × price per quintal.</p>
          <p className="mt-3 font-data text-xs text-slate-400">{farmerCount} farmer{farmerCount === 1 ? "" : "s"} tracked · As of {data.as_of}</p>
        </div>
      </div>
    </div>
  );
}

export default function Farmers() {
  const [farmers, setFarmers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", village: "", phone: "", note: "" });
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [removedFarmers, setRemovedFarmers] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(() => {
    setRefreshKey((k) => k + 1);
    return api.get("/farmers").then((r) => {
      setFarmers(r.data);
      if (!r.fromCache) saveFarmersForOffline(r.data);
    });
  }, []);
  const loadRemoved = useCallback(() => {
    return api.get("/farmers", { params: { include_inactive: true } })
      .then((r) => setRemovedFarmers(r.data.filter((f) => f.active === false)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showRemoved) loadRemoved(); }, [showRemoved, loadRemoved]);

  // Alphabetical by name, filtered by name/village/phone search.
  const visibleFarmers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? farmers.filter((f) =>
          f.name?.toLowerCase().includes(q) ||
          f.village?.toLowerCase().includes(q) ||
          f.phone?.toLowerCase().includes(q))
      : farmers;
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [farmers, search]);

  const addFarmer = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    await api.post("/farmers", form);
    toast.success("Farmer added");
    setForm({ name: "", village: "", phone: "", note: "" });
    setAddOpen(false); load();
  };
  const removeFarmer = async (id, hard) => {
    await api.delete(`/farmers/${id}`, { params: { hard: !!hard } });
    toast.success(hard ? "Farmer and all records deleted" : "Farmer removed — records kept, restore any time");
    if (activeId === id) setActiveId(null);
    load();
    if (!hard) loadRemoved();
  };
  const restoreFarmer = async (id) => {
    await api.post(`/farmers/${id}/restore`);
    toast.success("Farmer restored");
    load(); loadRemoved();
  };

  return (
    <div className="p-4 md:p-8 max-w-[1100px] mx-auto w-full space-y-6">
      <PageHeader title="Farmers" subtitle="Advances, interest and harvest settlements — overview and per-farmer detail in one place.">
        <Button data-testid="btn-farmers-report" variant="outline" onClick={() => setReportOpen(true)} className="gap-2">
          <FileText size={16} weight="bold" /> Overall Statement
        </Button>
        <Button data-testid="btn-add-farmer" onClick={() => setAddOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
          <Plus size={16} weight="bold" /> Add Farmer
        </Button>
      </PageHeader>

      <FarmersOverview farmerCount={farmers.length} refreshKey={refreshKey} />

      {farmers.length > 0 && (
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="farmer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search farmers by name, village or phone…"
            className="pl-9"
          />
        </div>
      )}

      {farmers.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <Plant size={40} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
          <p className="text-slate-600 font-medium">No farmers yet.</p>
          <p className="text-sm text-slate-500 mt-1">Add a farmer to start recording advances.</p>
        </div>
      )}

      {farmers.length > 0 && visibleFarmers.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <MagnifyingGlass size={32} weight="duotone" className="mx-auto text-slate-400 mb-2" />
          <p className="text-slate-600 font-medium">No farmers match "{search}".</p>
        </div>
      )}


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleFarmers.map((f) => {
          const nl = netLabel(f.net);
          return (
            <button key={f.id} data-testid={`farmer-card-${f.id}`} onClick={() => setActiveId(f.id)}
              className="text-left bg-white border border-slate-200 rounded-lg shadow-sm p-5 hover:border-slate-400 transition-colors group">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-800">{f.name}</div>
                  <div className="text-xs text-slate-400">{f.village || "—"}{f.phone ? ` · ${f.phone}` : ""}</div>
                </div>
                <CaretRight size={16} className="text-slate-300 group-hover:text-slate-600" />
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Net position</div>
                  <div className={`font-data text-lg font-semibold ${nl.cls}`}>{nl.text}</div>
                </div>
                <div className="text-right text-xs text-slate-400 font-data">
                  <div>Adv {fmtMoney(f.total_advance)}</div>
                  <div>Int {fmtMoney(f.total_interest)}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="border-t border-slate-200 pt-4">
        <button type="button" data-testid="btn-toggle-removed-farmers" onClick={() => setShowRemoved((v) => !v)}
          className="text-xs text-slate-400 hover:text-slate-600">
          {showRemoved ? "Hide" : "Show"} removed farmers{removedFarmers.length > 0 ? ` (${removedFarmers.length})` : ""}
        </button>

        {showRemoved && (
          <div className="mt-3">
            {removedFarmers.length === 0 && <p className="text-xs text-slate-400">No removed farmers.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {removedFarmers.map((f) => (
                <button key={f.id} data-testid={`removed-farmer-card-${f.id}`} onClick={() => setActiveId(f.id)}
                  className="text-left bg-slate-50 border border-dashed border-slate-300 rounded-lg p-5 hover:border-slate-400 transition-colors group opacity-80">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-600">{f.name}</div>
                      <div className="text-xs text-slate-400">{f.village || "—"}{f.phone ? ` · ${f.phone}` : ""}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded">Removed</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">Records kept — open to view or restore.</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Add Farmer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input data-testid="farmer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Suresh Gowda" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Village</Label>
                <Input data-testid="farmer-village" value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input data-testid="farmer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="btn-save-farmer" className="bg-emerald-600 hover:bg-emerald-700" onClick={addFarmer}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FarmerDetail farmerId={activeId} onClose={() => setActiveId(null)} onChanged={() => { load(); loadRemoved(); }}
        onDelete={removeFarmer} onRestore={restoreFarmer} />

      <FarmersReportDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}

function FarmerDetail({ farmerId, onClose, onChanged, onDelete, onRestore }) {
  const tenant = useTenant();
  // Lot being corrected in the weigh-bags form (null = adding a new lot).
  // Its own stamped modes win over the tenant's current ones, so editing
  // an old lot never re-weighs it under a setting it wasn't recorded with.
  const [editingHarvest, setEditingHarvest] = useState(null);
  const dried = (editingHarvest?.pricing_mode ?? tenant?.arecanut_pricing_mode) === "dried";
  const bagBatch = tenant?.arecanut_bag_batch || FALLBACK_BAG_BATCH;
  // "combined": this tenant's buyer quotes one deduction rate (kg/quintal)
  // covering both bag weight and wastage together -- no separate per-bag
  // tare step, so "Sodi" doubles as that combined rate (see server.py's
  // _harvest_metrics).
  const combinedDeduction = (editingHarvest?.deduction_mode ?? tenant?.arecanut_deduction_mode) === "combined";
  const [confirmDelete, setConfirmDelete] = useState(null); // see ConfirmDelete
  const [detail, setDetail] = useState(null);
  const [asOf, setAsOf] = useState(todayStr());
  const [adv, setAdv] = useState({ amount: "", rate: "12", date: todayStr(), note: "" });
  const [har, setHar] = useState({
    date: todayStr(), note: "", bags: [{ w: "", n: bagBatch }],
    bagTareKg: DEFAULT_BAG_TARE_KG, sodiKgPerQuintal: "", conversionKgPerQuintal: "",
  });
  const [priceTarget, setPriceTarget] = useState(null);
  const [priceForm, setPriceForm] = useState({
    price_per_quintal: "", sodi_kg_per_quintal: "",
    price_per_kg_dried: "", conversion_kg_per_quintal: "",
    driedPriceUnit: "kg", // "kg" or "quintal" -- which unit price_per_kg_dried's *input box* is in right now
  });
  const [hardDelete, setHardDelete] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleForm, setSettleForm] = useState({ date: todayStr(), note: "" });
  const [showStatement, setShowStatement] = useState(false);
  const [stmtPickerOpen, setStmtPickerOpen] = useState(false);
  const [stmtRange, setStmtRange] = useState({
    start: `${todayStr().slice(0, 4)}-01-01`,
    end: todayStr(),
  });
  const [stmtData, setStmtData] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [slipHarvest, setSlipHarvest] = useState(null);
  const [showSettledAdvances, setShowSettledAdvances] = useState(false);
  const [showSettledHarvests, setShowSettledHarvests] = useState(false);

  const [pending, setPending] = useState([]);

  const load = useCallback(() => {
    if (!farmerId) return;
    api.get(`/farmers/${farmerId}?as_of=${asOf}`).then((r) => setDetail(r.data))
      .catch((e) => { if (isNetworkError(e)) toast.error("No internet, and this farmer isn't saved on this device yet"); });
  }, [farmerId, asOf]);
  useEffect(() => { load(); }, [load]);

  // Lots weighed offline for this farmer, still waiting on this device.
  // When one uploads, reload so it shows up as a normal lot.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  useEffect(() => {
    if (!farmerId) return undefined;
    let count = pendingHarvests(farmerId).length;
    setPending(pendingHarvests(farmerId));
    const onChange = () => {
      const next = pendingHarvests(farmerId);
      if (next.length < count) { load(); onChangedRef.current(); }
      count = next.length;
      setPending(next);
    };
    window.addEventListener(PENDING_EVENT, onChange);
    return () => window.removeEventListener(PENDING_EVENT, onChange);
  }, [farmerId, load]);

  const addAdvance = async () => {
    if (!adv.amount || !adv.date) { toast.error("Amount and date required"); return; }
    await api.post(`/farmers/${farmerId}/advances`, { amount: Number(adv.amount), rate: Number(adv.rate) || 0, date: adv.date, note: adv.note });
    toast.success("Advance added");
    setAdv({ amount: "", rate: adv.rate, date: adv.date, note: "" });
    load(); onChanged();
  };
  const filledBagRows = har.bags.filter((b) => b.w !== "" && !isNaN(parseFloat(b.w)) && parseFloat(b.w) > 0);
  const bagCount = filledBagRows.reduce((s, b) => s + b.n, 0);
  const bagRawTotal = filledBagRows.reduce((s, b) => s + parseFloat(b.w), 0);
  const bagTareRate = combinedDeduction ? 0 : parseFloat(har.bagTareKg) || 0;
  const bagTareTotal = bagCount * bagTareRate;
  const bagTotal = Math.max(0, bagRawTotal - bagTareTotal);
  const bagSodiRate = parseFloat(har.sodiKgPerQuintal) || 0;
  const bagQuintals = bagTotal / 100;
  const bagNetQuintals = bagQuintals * (1 - bagSodiRate / 100);
  const bagConversionRate = parseFloat(har.conversionKgPerQuintal) || 0;
  // Dried mode deducts sodi first, same as quintal mode, then converts the
  // remaining (net) quintals to dried weight.
  const bagDriedKg = bagNetQuintals * bagConversionRate;
  const addHarvest = async () => {
    if (filledBagRows.length === 0) { toast.error("Enter at least one bag weight"); return; }
    const bags = filledBagRows.map((b) => ({ w: parseFloat(b.w), n: b.n }));
    const payload = {
      client_ref: newClientRef(),
      date: har.date, note: har.note, bags,
      bag_tare_kg: bagTareRate, sodi_kg_per_quintal: bagSodiRate,
      ...(dried ? { conversion_kg_per_quintal: bagConversionRate } : {}),
    };
    const resetForm = () => setHar({
      date: har.date, note: "", bags: [{ w: "", n: bagBatch }], bagTareKg: har.bagTareKg,
      sodiKgPerQuintal: har.sodiKgPerQuintal, conversionKgPerQuintal: har.conversionKgPerQuintal,
    });
    if (editingHarvest) {
      const { client_ref: _ref, ...changes } = payload;
      try {
        await api.put(`/harvests/${editingHarvest.id}`, changes);
      } catch (e) {
        toast.error(isNetworkError(e) ? "No internet — editing a saved lot needs a connection" : e?.response?.data?.detail || "Could not update harvest");
        return;
      }
      toast.success("Harvest updated");
      cancelEditHarvest();
      load(); onChanged();
      return;
    }
    // No network at the farm: keep the lot on this device and let
    // lib/offline.js upload it later (same client_ref, so never twice).
    const saveOnDevice = () => {
      try {
        queueHarvest({
          farmerId, farmerName: detail?.name, payload,
          summary: `${bagCount} bags · ${bagTotal.toFixed(2)} kg = ${bagNetQuintals.toFixed(4)} q (net)`
            + (dried && bagConversionRate > 0 ? ` → ${bagDriedKg.toFixed(3)} kg dried` : ""),
        });
      } catch {
        toast.error("Could not save on this device — storage is full or blocked. Harvest NOT saved.");
        return;
      }
      toast.success("No internet — harvest saved on this device. It uploads automatically once back online.");
      resetForm();
    };
    if (isOffline()) { saveOnDevice(); return; }
    try {
      await api.post(`/farmers/${farmerId}/harvests`, payload);
    } catch (e) {
      if (isNetworkError(e)) { saveOnDevice(); return; }
      toast.error(e?.response?.data?.detail || "Could not record harvest");
      return;
    }
    toast.success("Harvest recorded — set its price once known, before settling");
    resetForm();
    load(); onChanged();
  };
  const discardPending = (p) => setConfirmDelete({
    title: "Discard this offline lot?",
    description: `${p.summary} · ${p.payload.date}. It has not been uploaded yet and will be lost.`,
    onConfirm: () => discardPendingHarvest(p.client_ref),
  });
  // Loads a saved lot back into the weigh-bags form to correct readings or
  // add bags that came in later, with an empty row ready for the next bag.
  const startEditHarvest = (h) => {
    const rows = (h.bags || []).map((b) => (typeof b === "object" && b !== null
      ? { w: String(b.w), n: b.n || 1 } : { w: String(b), n: 1 }));
    setEditingHarvest(h);
    setHar({
      date: h.date, note: h.note || "", bags: [...rows, { w: "", n: bagBatch }],
      bagTareKg: h.bag_tare_kg != null ? String(h.bag_tare_kg) : DEFAULT_BAG_TARE_KG,
      sodiKgPerQuintal: h.sodi_kg_per_quintal ? String(h.sodi_kg_per_quintal) : "",
      conversionKgPerQuintal: h.conversion_kg_per_quintal != null ? String(h.conversion_kg_per_quintal) : "",
    });
    document.querySelector('[data-testid="harvest-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  function cancelEditHarvest() {
    setEditingHarvest(null);
    setHar((prev) => ({ ...prev, date: todayStr(), note: "", bags: [{ w: "", n: bagBatch }] }));
  }
  useEffect(() => { setEditingHarvest(null); }, [farmerId]);
  const askDeleteHarvest = (h) => setConfirmDelete({
    title: "Delete this harvest lot?",
    description: `${h.date} · ${h.bag_count ? `${h.bag_count} bags, ` : ""}${h.total_kg} kg (${h.quintals} q). This cannot be undone.`,
    onConfirm: () => delHarvest(h.id),
  });
  const delAdvance = async (id) => { await api.delete(`/advances/${id}`); toast.success("Advance deleted"); load(); onChanged(); };
  const askDeleteAdvance = (a) => setConfirmDelete({
    title: "Delete this advance?",
    description: `${fmtMoney(a.amount)} @ ${a.rate}% given on ${a.date}${a.note ? ` (${a.note})` : ""}. This cannot be undone.`,
    onConfirm: () => delAdvance(a.id),
  });
  const delHarvest = async (id) => {
    try {
      await api.delete(`/harvests/${id}`);
    } catch (e) {
      toast.error(isNetworkError(e) ? "No internet — could not delete" : e?.response?.data?.detail || "Could not delete harvest");
      return;
    }
    if (editingHarvest?.id === id) cancelEditHarvest();
    toast.success("Harvest deleted");
    load(); onChanged();
  };
  const openPriceDialog = (h) => {
    setPriceTarget(h);
    setPriceForm({
      price_per_quintal: h.price_per_quintal ?? "",
      sodi_kg_per_quintal: h.sodi_kg_per_quintal ?? priceForm.sodi_kg_per_quintal ?? "",
      // Always redisplayed in kg (the canonical stored unit) regardless of
      // which unit it was originally entered in -- switching the toggle
      // below converts it live.
      price_per_kg_dried: h.price_per_kg_dried ?? "",
      conversion_kg_per_quintal: h.conversion_kg_per_quintal ?? priceForm.conversion_kg_per_quintal ?? "",
      driedPriceUnit: "kg",
    });
  };
  // Market rate for dried arecanut is sometimes quoted per kg, sometimes
  // per quintal -- switching the toggle converts the number already typed
  // so it keeps meaning the same price, instead of silently changing it.
  const setDriedPriceUnit = (unit) => {
    if (unit === priceForm.driedPriceUnit) return;
    const n = Number(priceForm.price_per_kg_dried);
    const converted = priceForm.price_per_kg_dried === "" || isNaN(n) ? ""
      : unit === "quintal" ? String(n * 100) : String(n / 100);
    setPriceForm({ ...priceForm, driedPriceUnit: unit, price_per_kg_dried: converted });
  };
  const savePrice = async () => {
    const lotDried = (priceTarget?.pricing_mode || "quintal") === "dried";
    // Dried-mode lots are usually settled with the dried arecanut itself,
    // so price_per_kg_dried is optional there -- only the conversion factor
    // (needed to know the dried weight) matters. Quintal-mode lots are
    // always cash-priced, so price_per_quintal stays required.
    if (!lotDried && (!priceForm.price_per_quintal || Number(priceForm.price_per_quintal) <= 0)) {
      toast.error("Enter price per quintal"); return;
    }
    if (lotDried && !priceForm.conversion_kg_per_quintal && priceTarget.conversion_kg_per_quintal == null) {
      toast.error("Enter the conversion factor (kg dried per quintal)"); return;
    }
    try {
      const driedPriceField = priceForm.driedPriceUnit === "quintal" ? "price_per_quintal_dried" : "price_per_kg_dried";
      const body = lotDried
        ? {
            ...(priceForm.price_per_kg_dried !== "" ? { [driedPriceField]: Number(priceForm.price_per_kg_dried) } : {}),
            ...(priceForm.conversion_kg_per_quintal !== "" ? { conversion_kg_per_quintal: Number(priceForm.conversion_kg_per_quintal) } : {}),
            sodi_kg_per_quintal: Number(priceForm.sodi_kg_per_quintal) || 0,
          }
        : {
            price_per_quintal: Number(priceForm.price_per_quintal),
            sodi_kg_per_quintal: Number(priceForm.sodi_kg_per_quintal) || 0,
          };
      await api.put(`/harvests/${priceTarget.id}/price`, body);
      toast.success(lotDried && priceForm.price_per_kg_dried === "" ? "Conversion saved" : "Lot priced");
      setPriceTarget(null);
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set price");
    }
  };
  const doSettle = async () => {
    try {
      await api.post(`/farmers/${farmerId}/settle`, settleForm);
      toast.success("Settlement recorded — interest frozen");
      setSettleOpen(false); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing to settle"); }
  };
  const delSettlement = async (id) => { await api.delete(`/settlements/${id}`); toast.success("Settlement undone"); load(); onChanged(); };
  const askDeleteSettlement = (st) => setConfirmDelete({
    title: "Undo this settlement?",
    description: `Settlement of ${st.date} (${netLabel(st.net).text}). Its advances and harvest lots go back to running, and interest starts accruing again.`,
    confirmLabel: "Undo Settlement",
    onConfirm: () => delSettlement(st.id),
  });

  const generateStatement = async () => {
    if (!stmtRange.start || !stmtRange.end) { toast.error("Pick both a from and to date"); return; }
    if (stmtRange.start > stmtRange.end) { toast.error("From date must be before To date"); return; }
    try {
      setStmtLoading(true);
      const r = await api.get(`/farmers/${farmerId}/statement`, { params: stmtRange });
      setStmtData(r.data);
      setStmtPickerOpen(false);
      setShowStatement(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate statement");
    } finally {
      setStmtLoading(false);
    }
  };

  const setBag = (i, v) => {
    const b = [...har.bags];
    b[i] = { ...b[i], w: v };
    if (i === b.length - 1 && v) b.push({ w: "", n: bagBatch });
    setHar({ ...har, bags: b });
  };
  const toggleBagPair = (i) => {
    const b = [...har.bags];
    const cycle = bagBatchCycle(bagBatch);
    const next = cycle[(cycle.indexOf(b[i].n) + 1) % cycle.length];
    b[i] = { ...b[i], n: next };
    setHar({ ...har, bags: b });
  };
  const removeBag = (i) => {
    const b = har.bags.filter((_, idx) => idx !== i);
    setHar({ ...har, bags: b.length ? b : [{ w: "", n: bagBatch }] });
  };
  const setBagBatch = async (n) => {
    try {
      await api.put("/tenant/settings", { arecanut_bag_batch: n });
      tenant?._refresh?.();
      // Rows already on the form were stamped with the old default when
      // they were created -- tenant._refresh() only changes what *new*
      // rows get, so any not-yet-weighed row needs updating right away too,
      // otherwise it silently keeps the old batch size until the page reloads.
      setHar((prev) => ({ ...prev, bags: prev.bags.map((b) => (b.w === "" ? { ...b, n } : b)) }));
      toast.success(`Now weighing ${n} bags at a time by default`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save setting");
    }
  };

  const s = detail?.summary;
  const nl = s ? netLabel(s.net) : null;
  const pendingPriceHarvests = s ? s.harvests.filter((h) => !h.settled && !h.priced && h.quintals > 0) : [];

  return (
    <>
    <Sheet open={!!farmerId && !showStatement && !slipHarvest} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="farmer-detail">
        {detail && (
          <div>
            <SheetHeader className="p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
  <div className="flex items-center justify-between">

    {/* Back button + Farmer name */}
    <div className="flex items-center gap-2 min-w-0">
      <button
        type="button"
        onClick={onClose}
        aria-label="Back to farmers"
        className="h-9 w-9 flex items-center justify-center rounded-md
                   text-slate-600 hover:bg-slate-100 hover:text-slate-900
                   transition-colors shrink-0"
      >
        <CaretLeft size={20} weight="bold" />
      </button>

      <SheetTitle className="text-xl truncate">
        {detail.name}
      </SheetTitle>
    </div>

    {/* Statement + Restore/Delete */}
    <div className="flex items-center gap-1 shrink-0">
      <button
        data-testid="btn-farmer-statement"
        onClick={() => setStmtPickerOpen(true)}
        className="h-8 px-2 flex items-center gap-1 text-xs rounded-md
                   text-slate-600 hover:bg-slate-100"
      >
        <FileText size={14} /> Statement
      </button>

      {detail.active === false && (
        <button
          data-testid="btn-restore-farmer"
          onClick={() => onRestore(detail.id)}
          className="h-8 px-2 flex items-center gap-1 text-xs rounded-md
                     text-emerald-600 hover:bg-emerald-50"
        >
          <ArrowCounterClockwise size={14} /> Restore
        </button>
      )}

      <AlertDialog onOpenChange={(o) => { if (o) setHardDelete(false); }}>
        <AlertDialogTrigger asChild>
          <button
            data-testid="btn-delete-farmer"
            className="h-8 px-2 flex items-center gap-1 text-xs rounded-md
                       text-rose-500 hover:bg-rose-50"
          >
            <Trash size={14} /> Delete
          </button>
        </AlertDialogTrigger>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {detail.name}?
            </AlertDialogTitle>

            <AlertDialogDescription>
              {detail.active === false
                ? "This farmer is already removed from your list. This deletes their advances, harvests and settlements permanently."
                : "Removes the farmer from your list. Their advances, harvests and settlements are kept in the records unless you choose to erase them below."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {detail.active !== false && (
            <label className="flex items-start gap-2.5 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm cursor-pointer">
              <Checkbox
                data-testid="chk-hard-delete-farmer"
                checked={hardDelete}
                onCheckedChange={(v) => setHardDelete(!!v)}
                className="mt-0.5"
              />
              <span className="text-rose-700">
                Also permanently delete all of this farmer's advances, harvest lots and settlements.
                <span className="block text-xs text-rose-500 mt-0.5">This cannot be undone.</span>
              </span>
            </label>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              data-testid="btn-confirm-delete-farmer"
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => onDelete(detail.id, detail.active === false ? true : hardDelete)}
            >
              {detail.active === false || hardDelete ? "Delete Everything" : "Delete Farmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </div>

  <p className="text-sm text-slate-400 text-left ml-11">
    {detail.village || "—"}
    {detail.phone ? ` · ${detail.phone}` : ""}
  </p>
</SheetHeader>

            <div className="p-5 space-y-6">
              {/* Settlement summary */}
              <div className="rounded-lg border border-slate-200 bg-slate-900 text-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-widest text-slate-400">Current position as of</span>
                  <Input data-testid="asof-date" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                    className="w-36 h-8 bg-slate-800 border-slate-700 text-white font-data" />
                </div>
                <div className="grid grid-cols-2 gap-y-2 font-data text-sm">
                  <span className="text-slate-400">Total advance</span><span className="text-right" data-testid="sum-advance">{fmtMoney(s.total_advance)}</span>
                  <span className="text-slate-400">+ Interest</span><span className="text-right" data-testid="sum-interest">{fmtMoney(s.total_interest)}</span>
                  <span className="text-slate-400">Owed by farmer</span><span className="text-right font-semibold" data-testid="sum-owed">{fmtMoney(s.owed_by_farmer)}</span>
                  <span className="text-slate-400">− Harvest value</span>
                  <span className="text-right" data-testid="sum-harvest">
                    {fmtMoney(s.total_harvest_value)}
                    {pendingPriceHarvests.length > 0 && <span className="text-amber-300 text-xs"> (+pending)</span>}
                  </span>
                </div>
                <div className="border-t border-slate-700 mt-3 pt-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-slate-400">Net</span>
                  <span className={`font-data text-lg font-semibold ${s.net > 0 ? "text-emerald-400" : s.net < 0 ? "text-rose-400" : "text-slate-300"}`} data-testid="sum-net">{nl.text}</span>
                </div>
                <Button data-testid="btn-settle" disabled={pendingPriceHarvests.length > 0}
                  onClick={() => { setSettleForm({ date: asOf, note: "" }); setSettleOpen(true); }}
                  className="w-full mt-4 bg-amber-400 hover:bg-amber-500 text-slate-900 gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                  <Handshake size={18} weight="bold" /> Settle Now
                </Button>
                {pendingPriceHarvests.length > 0 && (
                  <p className="text-xs text-amber-300 mt-2 text-center">
                    Price {pendingPriceHarvests.length} pending harvest lot{pendingPriceHarvests.length === 1 ? "" : "s"} below before settling
                  </p>
                )}
              </div>

              {/* Advances */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><Money size={16} /> Advances</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                  <Input data-testid="adv-amount" type="number" placeholder="Amount ₹" className="font-data" value={adv.amount} onChange={(e) => setAdv({ ...adv, amount: e.target.value })} />
                  <Input data-testid="adv-rate" type="number" placeholder="Rate % / yr" className="font-data" value={adv.rate} onChange={(e) => setAdv({ ...adv, rate: e.target.value })} />
                  <Input data-testid="adv-date" type="date" className="font-data" value={adv.date} onChange={(e) => setAdv({ ...adv, date: e.target.value })} />
                </div>
                {Number(adv.amount) > 0 && (
                  <p data-testid="adv-amount-words" className="text-xs text-slate-500 italic mb-2 -mt-1">
                    ₹ {amountInWords(adv.amount)}
                  </p>
                )}
                <div className="flex gap-2 mb-3">
                  <Input data-testid="adv-note" placeholder="Description / note (optional)" value={adv.note} onChange={(e) => setAdv({ ...adv, note: e.target.value })} />
                  <Button data-testid="btn-add-advance" className="bg-slate-900" onClick={addAdvance}>Add</Button>
                </div>
                <div className="space-y-1.5">
                  {s.advances.filter((a) => !a.settled).map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 text-sm bg-white border border-slate-200 rounded-md px-3 py-2 font-data" data-testid={`advance-${a.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-700">{fmtMoney(a.amount)} @ {a.rate}% <span className="text-orange-600 text-xs">+{fmtMoney(a.accrued_interest)} int</span></div>
                        <div className="text-xs text-slate-400 truncate">{a.date}{a.note ? ` · ${a.note}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button data-testid={`wa-advance-${a.id}`} aria-label="Send advance on WhatsApp"
                          onClick={() => openWa(`*Advance received — ${detail.name}*\nDate: ${a.date}\nAmount: ${fmtMoney(a.amount)}\nInterest: ${a.rate}% per year${a.note ? `\nNote: ${a.note}` : ""}`, detail.phone)}
                          className="text-emerald-600 hover:text-emerald-700"><WhatsappLogo size={16} weight="fill" /></button>
                        <button data-testid={`del-advance-${a.id}`} aria-label="Delete advance" onClick={() => askDeleteAdvance(a)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                  {s.advances.filter((a) => !a.settled).length === 0 && <p className="text-xs text-slate-400">No running advances.</p>}

                  {s.advances.some((a) => a.settled) && (
                    <button type="button" onClick={() => setShowSettledAdvances((v) => !v)}
                      className="text-xs text-slate-400 hover:text-slate-600 pt-1">
                      {showSettledAdvances ? "Hide" : "Show"} {s.advances.filter((a) => a.settled).length} settled advance{s.advances.filter((a) => a.settled).length === 1 ? "" : "s"}
                    </button>
                  )}

                  {showSettledAdvances && s.advances.filter((a) => a.settled).map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-data opacity-75" data-testid={`advance-${a.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-600">{fmtMoney(a.amount)} @ {a.rate}% <span className="text-orange-500 text-xs">+{fmtMoney(a.accrued_interest)} int</span></div>
                        <div className="text-xs text-slate-400 truncate">{a.date}{a.note ? ` · ${a.note}` : ""} · settled</div>
                      </div>
                      <span className="text-[10px] uppercase text-slate-400">locked</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Harvests - bag weighing */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><Plant size={16} /> Harvest Deliveries</h3>
                <div data-testid="harvest-form" className={`border rounded-md p-3 mb-3 scroll-mt-24 ${editingHarvest ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                  {editingHarvest && (
                    <div className="flex items-center justify-between gap-2 mb-2 text-xs font-semibold text-amber-800" data-testid="harvest-editing-banner">
                      <span>Editing lot of {editingHarvest.date} — correct or add bags, then save</span>
                      <button type="button" onClick={cancelEditHarvest} className="text-amber-700 hover:text-amber-900 underline">Cancel</button>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <span className="text-xs font-semibold text-slate-600">Weigh bags (Kg) — {bagBatch} at a time, tap ×{bagBatch} to correct a leftover</span>
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-md border border-slate-300 overflow-hidden" title="Default bags weighed together at once for this business">
                        {[2, 4].map((n) => (
                          <button key={n} type="button" data-testid={`bag-batch-${n}`} onClick={() => setBagBatch(n)}
                            className={`px-2 py-0.5 text-[10px] font-data ${bagBatch === n ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                            {n} at a time
                          </button>
                        ))}
                      </div>
                      <span className="font-data text-xs text-slate-500">{bagCount} bags</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto pr-1">
                    {(() => {
                      let counter = 0;
                      return har.bags.map((b, i) => {
                        let label = "";
                        if (b.w !== "") {
                          const start = counter + 1;
                          counter += b.n;
                          label = b.n > 1 ? `${start}-${start + b.n - 1}` : `${start}`;
                        }
                        return (
                          <div key={i} className="relative">
                            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-data">{label || i + 1}</span>
                            <Input data-testid={`bag-${i}`} type="number" value={b.w} onChange={(e) => setBag(i, e.target.value)}
                              className="pl-10 pr-12 h-8 font-data text-xs" placeholder="kg" />
                            {b.w !== "" && (
                              <button type="button" data-testid={`bag-pair-toggle-${i}`} onClick={() => toggleBagPair(i)}
                                title={`Tap to cycle how many bags this reading covers (${bagBatchCycle(bagBatch).join("/")})`}
                                className={`absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-data px-1 rounded ${b.n > 1 ? "text-emerald-600 bg-emerald-50" : "text-slate-400 bg-slate-100"}`}>
                                ×{b.n}
                              </button>
                            )}
                            {b.w !== "" && <button onClick={() => removeBag(i)} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-300 hover:text-rose-500"><X size={12} /></button>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div className={`grid gap-2 mt-3 ${
                    (combinedDeduction ? 1 : 2) + (dried ? 1 : 0) === 3 ? "grid-cols-3"
                      : (combinedDeduction ? 1 : 2) + (dried ? 1 : 0) === 2 ? "grid-cols-2" : "grid-cols-1"
                  }`}>
                    {!combinedDeduction && (
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-500">Bag tare (kg/bag)</Label>
                        <Input data-testid="har-bag-tare" type="number" step="0.01" className="font-data h-8 text-xs" placeholder="0.75"
                          value={har.bagTareKg} onChange={(e) => setHar({ ...har, bagTareKg: e.target.value })} />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-500">{combinedDeduction ? "Tare + Sodi (kg/quintal)" : "Sodi (kg/quintal)"}</Label>
                      <Input data-testid="har-sodi" type="number" step="0.01" className="font-data h-8 text-xs" placeholder="e.g. 1"
                        value={har.sodiKgPerQuintal} onChange={(e) => setHar({ ...har, sodiKgPerQuintal: e.target.value })} />
                    </div>
                    {dried && (
                      <div className="space-y-1">
                        <Label className="text-[10px] text-slate-500">Conversion (kg dried/quintal)</Label>
                        <Input data-testid="har-conversion" type="number" step="0.01" className="font-data h-8 text-xs" placeholder="e.g. 12.5"
                          value={har.conversionKgPerQuintal} onChange={(e) => setHar({ ...har, conversionKgPerQuintal: e.target.value })} />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    <Input data-testid="har-date" type="date" className="font-data" value={har.date} onChange={(e) => setHar({ ...har, date: e.target.value })} />
                  </div>
                  <Input data-testid="har-note" placeholder="Lot note (optional)" className="mt-2" value={har.note} onChange={(e) => setHar({ ...har, note: e.target.value })} />
                  <div className="flex items-center justify-between mt-3 font-data text-sm">
                    <span className="text-slate-600" data-testid="bag-total">
                      {dried
                        ? `Total: ${bagTotal.toFixed(2)} kg = ${bagNetQuintals.toFixed(4)} q (net) → ${bagDriedKg.toFixed(3)} kg dried`
                        : `Total: ${bagTotal.toFixed(2)} kg = ${bagNetQuintals.toFixed(4)} q (net)`}
                    </span>
                    <span className="text-slate-400 text-xs">Price set per lot, at settlement</span>
                  </div>
                  {bagCount > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1" data-testid="bag-tare-breakdown">
                      Raw {bagRawTotal.toFixed(2)} kg
                      {!combinedDeduction ? ` − tare ${bagCount} × ${bagTareRate}kg = ${bagTotal.toFixed(2)} kg` : ""}
                      {` = ${bagQuintals.toFixed(4)} q`}
                      {bagSodiRate > 0 ? ` − ${combinedDeduction ? "tare+sodi" : "sodi"} ${bagSodiRate}kg/q → ${bagNetQuintals.toFixed(4)} q` : ""}
                      {dried && bagConversionRate > 0 ? ` × ${bagConversionRate} kg/q → ${bagDriedKg.toFixed(3)} kg dried` : ""}
                    </div>
                  )}
                  {editingHarvest ? (
                    <div className="flex gap-2 mt-2">
                      <Button data-testid="btn-cancel-edit-harvest" variant="outline" className="flex-1" onClick={cancelEditHarvest}>Cancel</Button>
                      <Button data-testid="btn-save-harvest" className="flex-[2] bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={addHarvest}>Save Changes</Button>
                    </div>
                  ) : (
                    <Button data-testid="btn-add-harvest" className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700" onClick={addHarvest}>Add Harvest</Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {pending.map((p) => (
                    <div key={p.client_ref} className="flex items-center justify-between gap-2 text-sm bg-sky-50 border border-sky-200 rounded-md px-3 py-2 font-data" data-testid={`pending-harvest-${p.client_ref}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-700">
                          {p.summary} <span className="text-sky-700 font-medium">· saved on this device, waiting to upload</span>
                        </div>
                        <div className="text-xs text-slate-400 truncate">{p.payload.date}{p.payload.note ? ` · ${p.payload.note}` : ""}</div>
                        {p.error && <div className="text-xs text-rose-600">Upload refused: {p.error}</div>}
                      </div>
                      <button data-testid={`discard-pending-${p.client_ref}`} aria-label="Discard offline lot" onClick={() => discardPending(p)}
                        className="text-rose-500 hover:text-rose-700 shrink-0"><Trash size={14} /></button>
                    </div>
                  ))}
                  {s.harvests.filter((h) => !h.settled).map((h) => (
                    <div key={h.id} className="flex items-center justify-between gap-2 text-sm bg-white border border-slate-200 rounded-md px-3 py-2 font-data" data-testid={`harvest-${h.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-700">
                          {harvestSummaryLine(h)}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {h.date}{h.note ? ` · ${h.note}` : ""}
                          {h.bag_count ? ` · ${h.bag_count} bags, raw ${h.raw_kg}kg − tare ${h.tare_kg}kg` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {h.priced ? (
                          <button data-testid={`edit-price-harvest-${h.id}`} aria-label="Edit price" title="Edit price" onClick={() => openPriceDialog(h)}
                            className="text-slate-500 hover:text-slate-800"><Coins size={15} /></button>
                        ) : (
                          <Button data-testid={`price-harvest-${h.id}`} size="sm" className="h-7 px-2 bg-amber-400 hover:bg-amber-500 text-slate-900 gap-1"
                            onClick={() => openPriceDialog(h)}><Coins size={14} weight="bold" /> Set Price</Button>
                        )}
                        <button data-testid={`slip-harvest-${h.id}`} aria-label="Harvest slip" onClick={() => setSlipHarvest(h)}
                          className="text-slate-500 hover:text-slate-800"><FileText size={15} /></button>
                        <button data-testid={`edit-harvest-${h.id}`} aria-label="Edit bags" title="Edit bags / add bags" onClick={() => startEditHarvest(h)}
                          className={editingHarvest?.id === h.id ? "text-amber-600" : "text-slate-500 hover:text-slate-800"}><PencilSimple size={15} /></button>
                        <button data-testid={`del-harvest-${h.id}`} aria-label="Delete harvest" title="Delete" onClick={() => askDeleteHarvest(h)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                  {s.harvests.filter((h) => !h.settled).length === 0 && pending.length === 0 && <p className="text-xs text-slate-400">No running harvest deliveries.</p>}

                  {s.harvests.some((h) => h.settled) && (
                    <button type="button" onClick={() => setShowSettledHarvests((v) => !v)}
                      className="text-xs text-slate-400 hover:text-slate-600 pt-1">
                      {showSettledHarvests ? "Hide" : "Show"} {s.harvests.filter((h) => h.settled).length} settled deliver{s.harvests.filter((h) => h.settled).length === 1 ? "y" : "ies"}
                    </button>
                  )}

                  {showSettledHarvests && s.harvests.filter((h) => h.settled).map((h) => (
                    <div key={h.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-data opacity-75" data-testid={`harvest-${h.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-600">
                          {harvestSummaryLine(h)}
                        </div>
                        <div className="text-xs text-slate-400 truncate">{h.date}{h.note ? ` · ${h.note}` : ""} · settled</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button data-testid={`slip-harvest-${h.id}`} aria-label="Harvest slip" onClick={() => setSlipHarvest(h)}
                          className="text-slate-500 hover:text-slate-800"><FileText size={15} /></button>
                        <span className="text-[10px] uppercase text-slate-400">locked</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Settlements history */}
              {s.settlements.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><Handshake size={16} /> Settlements</h3>
                  <div className="space-y-1.5">
                    {s.settlements.map((st) => (
                      <div key={st.id} className="flex items-center justify-between gap-2 text-sm bg-white border border-slate-200 rounded-md px-3 py-2 font-data" data-testid={`settlement-${st.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className={st.net > 0 ? "text-emerald-700" : st.net < 0 ? "text-rose-700" : "text-slate-500"}>
                            {netLabel(st.net).text}
                          </div>
                          <div className="text-xs text-slate-400 truncate">{st.date} · adv {fmtMoney(st.total_advance)} + int {fmtMoney(st.total_interest)} − harvest {fmtMoney(st.total_harvest_value)}{st.note ? ` · ${st.note}` : ""}</div>
                        </div>
                        <button data-testid={`del-settlement-${st.id}`} aria-label="Undo settlement" onClick={() => askDeleteSettlement(st)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>

    {/* Settle dialog */}
    <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader><DialogTitle>Settle Account</DialogTitle></DialogHeader>
        {s && (
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-4 font-data text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Net to settle</span><span className={s.net > 0 ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{netLabel(s.net).text}</span></div>
            </div>
            <p className="text-xs text-slate-500">Settling freezes interest on the date below and locks the current advances & harvests. New entries after this start fresh.</p>
            <div className="space-y-1.5"><Label>Settlement date</Label>
              <Input data-testid="settle-date" type="date" className="font-data" value={settleForm.date} onChange={(e) => setSettleForm({ ...settleForm, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Note (optional)</Label>
              <Input data-testid="settle-note" value={settleForm.note} onChange={(e) => setSettleForm({ ...settleForm, note: e.target.value })} placeholder="e.g. paid by cash" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSettleOpen(false)}>Cancel</Button>
          <Button data-testid="btn-confirm-settle" className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold" onClick={doSettle}>Confirm Settle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Confirm before deleting an advance, harvest lot or settlement (or discarding an offline lot) */}
    <ConfirmDelete target={confirmDelete} onClose={() => setConfirmDelete(null)} />

    {/* Price a harvest lot */}
    <Dialog open={!!priceTarget} onOpenChange={(o) => { if (!o) setPriceTarget(null); }}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader><DialogTitle>Price This Lot</DialogTitle></DialogHeader>
        {priceTarget && (
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 font-data text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Lot weight</span><span>{priceTarget.total_kg} kg ({priceTarget.quintals} q)</span></div>
              <div className="text-xs text-slate-400 mt-0.5">{priceTarget.date}{priceTarget.note ? ` · ${priceTarget.note}` : ""}</div>
            </div>

            {priceTarget.pricing_mode === "dried" ? (
              <>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Price {priceForm.driedPriceUnit === "quintal" ? "per Quintal dried" : "per KG dried"} (₹) — optional</Label>
                    <div className="flex rounded-md border border-slate-300 overflow-hidden">
                      {[{ v: "kg", l: "kg" }, { v: "quintal", l: "quintal" }].map((u) => (
                        <button key={u.v} type="button" data-testid={`dried-price-unit-${u.v}`} onClick={() => setDriedPriceUnit(u.v)}
                          className={`px-2 py-0.5 text-[10px] font-data ${priceForm.driedPriceUnit === u.v ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                          /{u.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Input data-testid="price-rate" type="number" className="font-data" placeholder="Leave blank if settled in dried arecanut"
                    value={priceForm.price_per_kg_dried}
                    onChange={(e) => setPriceForm({ ...priceForm, price_per_kg_dried: e.target.value })} />
                  <p className="text-xs text-slate-400">Most lots are settled by handing over the dried arecanut itself — only fill this in if a cash price applies. Switch the unit above if the market rate you have is per quintal instead.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Sodi — wastage deducted (kg per quintal)</Label>
                  <Input data-testid="price-sodi" type="number" className="font-data" placeholder="e.g. 1"
                    value={priceForm.sodi_kg_per_quintal}
                    onChange={(e) => setPriceForm({ ...priceForm, sodi_kg_per_quintal: e.target.value })} />
                  <p className="text-xs text-slate-400">Carried over from weigh-in — change only to correct it. Deducted before conversion.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Conversion — kg dried per quintal (net, after sodi)</Label>
                  <Input data-testid="price-conversion" type="number" className="font-data" placeholder="e.g. 12.5"
                    value={priceForm.conversion_kg_per_quintal}
                    onChange={(e) => setPriceForm({ ...priceForm, conversion_kg_per_quintal: e.target.value })} />
                  <p className="text-xs text-slate-400">Carried over from weigh-in — change only to correct it.</p>
                </div>
                {priceForm.price_per_kg_dried > 0 && (() => {
                  const sodi = Number(priceForm.sodi_kg_per_quintal) || 0;
                  const netQ = priceTarget.quintals * (1 - sodi / 100);
                  const conv = Number(priceForm.conversion_kg_per_quintal) || 0;
                  const driedKg = netQ * conv;
                  const pricePerKg = priceForm.driedPriceUnit === "quintal"
                    ? Number(priceForm.price_per_kg_dried) / 100
                    : Number(priceForm.price_per_kg_dried);
                  const value = driedKg * pricePerKg;
                  return (
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 font-data text-sm flex justify-between">
                      <span className="text-slate-600">Net {netQ.toFixed(4)} q → {driedKg.toFixed(3)} kg dried</span>
                      <span className="text-emerald-700 font-semibold">≈ {fmtMoney(value)}</span>
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>Price per quintal (₹)</Label>
                  <Input data-testid="price-rate" type="number" className="font-data" placeholder="0"
                    value={priceForm.price_per_quintal}
                    onChange={(e) => setPriceForm({ ...priceForm, price_per_quintal: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Sodi — wastage deducted (kg per quintal)</Label>
                  <Input data-testid="price-sodi" type="number" className="font-data" placeholder="e.g. 1"
                    value={priceForm.sodi_kg_per_quintal}
                    onChange={(e) => setPriceForm({ ...priceForm, sodi_kg_per_quintal: e.target.value })} />
                  <p className="text-xs text-slate-400">Carried over from weigh-in — change only to correct it.</p>
                </div>
                {priceForm.price_per_quintal > 0 && (() => {
                  const sodi = Number(priceForm.sodi_kg_per_quintal) || 0;
                  const netQ = priceTarget.quintals * (1 - sodi / 100);
                  const value = netQ * Number(priceForm.price_per_quintal);
                  return (
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 font-data text-sm flex justify-between">
                      <span className="text-slate-600">Net {netQ.toFixed(4)} q</span>
                      <span className="text-emerald-700 font-semibold">≈ {fmtMoney(value)}</span>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setPriceTarget(null)}>Cancel</Button>
          <Button data-testid="btn-save-price" className="bg-emerald-600 hover:bg-emerald-700" onClick={savePrice}>Save Price</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Statement date-range picker */}
    <Dialog open={stmtPickerOpen} onOpenChange={setStmtPickerOpen}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader><DialogTitle>Generate Statement</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-slate-500">Pick the period to include. Opening/closing balance accounts for everything before this range.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input data-testid="stmt-start" type="date" className="font-data" value={stmtRange.start}
                onChange={(e) => setStmtRange({ ...stmtRange, start: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input data-testid="stmt-end" type="date" className="font-data" value={stmtRange.end}
                onChange={(e) => setStmtRange({ ...stmtRange, end: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setStmtPickerOpen(false)}>Cancel</Button>
          <Button data-testid="btn-generate-statement" className="bg-slate-900 hover:bg-slate-800" disabled={stmtLoading} onClick={generateStatement}>
            {stmtLoading ? "Generating..." : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Farmer statement */}
    {stmtData && (
      <PrintableStatement open={showStatement} onClose={() => setShowStatement(false)} testid="farmer-statement"
        shareText={() => buildFarmerShare(stmtData)}
        pdfFileName={`statement_${(stmtData.farmer.name || "farmer").replace(/\s+/g, "_")}_${stmtData.start}_to_${stmtData.end}.pdf`}>
        <FarmerStatementBody stmt={stmtData} />
      </PrintableStatement>
    )}

    {detail && slipHarvest && (
      <PrintableStatement open={!!slipHarvest} onClose={() => setSlipHarvest(null)} testid="harvest-slip"
        shareText={() => buildHarvestShare(detail, slipHarvest)}
        pdfFileName={`weighing_slip_${(detail.name || "farmer").replace(/\s+/g, "_")}_${slipHarvest.date}.pdf`}>
        <HarvestSlipBody farmer={detail} harvest={slipHarvest} />
      </PrintableStatement>
    )}
    </>
  );
}

// Actual weight sodi removes from one lot -- sodi_kg_per_quintal is a rate
// (kg deducted per quintal of raw weight, i.e. effectively a percentage),
// so the real amount taken off this lot is quintals * that rate.
function sodiDeductedKg(h) {
  return ((h.quintals || 0) * (h.sodi_kg_per_quintal || 0)).toFixed(2);
}

// One harvest lot's weight/price summary line, for both the live list and
// the settled/locked list. Branches on the lot's own pricing_mode (stamped
// at weigh-in) rather than the tenant's *current* setting, so a lot always
// displays the math it was actually priced with, even if the tenant later
// switches modes.
function harvestSummaryLine(h) {
  const sodiLabel = h.deduction_mode === "combined" ? "tare+sodi" : "sodi";
  if (h.pricing_mode === "dried") {
    const sodiPart = h.sodi_kg_per_quintal ? ` − ${sodiLabel} ${h.sodi_kg_per_quintal}kg/q → ${h.net_quintals} q` : "";
    const hasConversion = h.conversion_kg_per_quintal != null && h.dried_kg != null;
    const convPart = hasConversion
      ? ` → ${h.dried_kg} kg dried (${h.conversion_kg_per_quintal} kg/q)`
      : "";
    if (!h.priced) {
      return <>{h.total_kg} kg ({h.quintals} q){sodiPart}{convPart} <span className="text-amber-600 font-medium">· not priced yet</span></>;
    }
    // Ready to settle, but a cash price is optional in dried mode -- most
    // lots are handed over as dried arecanut itself, with no price entered.
    if (h.value == null) {
      return <>{h.total_kg} kg ({h.quintals} q){sodiPart}{convPart} <span className="text-slate-500 font-medium">· settled in dried arecanut, no cash price</span></>;
    }
    return <>{h.total_kg} kg ({h.quintals} q){sodiPart}{convPart} × {fmtMoney(h.price_per_kg_dried)}/kg = <span className="text-emerald-700 font-medium">{fmtMoney(h.value)}</span></>;
  }
  return h.priced ? (
    <>{h.total_kg} kg ({h.quintals} q) − {sodiLabel} {h.sodi_kg_per_quintal}kg/q → {h.net_quintals} q × {fmtMoney(h.price_per_quintal)} = <span className="text-emerald-700 font-medium">{fmtMoney(h.value)}</span></>
  ) : (
    <>{h.total_kg} kg ({h.quintals} q) − {sodiLabel} {h.sodi_kg_per_quintal || 0}kg/q → {h.net_quintals} q <span className="text-amber-600 font-medium">· not priced yet</span></>
  );
}

// Normalizes harvest.bags into { label, w } rows. Handles both the
// current {w, n} weighing-entry shape (bags weighed several at a time) and
// the flat-number shape older harvest records were saved with.
function bagLines(bags) {
  let counter = 0;
  return (bags || []).map((b) => {
    const isPair = typeof b === "object" && b !== null;
    const w = isPair ? b.w : b;
    const n = isPair ? (b.n || 1) : 1;
    const start = counter + 1;
    counter += n;
    return { label: n > 1 ? `${start}-${start + n - 1}` : `${start}`, w };
  });
}

function FarmersReportDialog({ open, onClose }) {
  const [asOf, setAsOf] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStatement, setShowStatement] = useState(false);

  const generate = async () => {
    try {
      setLoading(true);
      const r = await api.get("/arecanut/report", { params: { as_of: asOf } });
      setData(r.data);
      setShowStatement(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate statement");
    } finally {
      setLoading(false);
    }
  };

  const shareText = () => {
    if (!data) return "";
    return `*Arecanut Farmers Statement — as of ${data.as_of}*\n${data.rows.length} farmers · *${netLabel(data.totals.net).text}*\n\n📎 Full statement attached as PDF`;
  };

  return (
    <>
      <Dialog open={open && !showStatement} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader><DialogTitle>Overall Farmers Statement</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">Every farmer's current advance, interest and harvest position as of the date below.</p>
            <div className="space-y-1.5">
              <Label>As of</Label>
              <Input data-testid="report-asof" type="date" className="font-data" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="btn-generate-report" className="bg-slate-900 hover:bg-slate-800" disabled={loading} onClick={generate}>
              {loading ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {data && (
        <PrintableStatement open={showStatement} onClose={() => { setShowStatement(false); onClose(); }}
          testid="farmers-overall-statement" shareText={shareText}
          onExcel={() => downloadFile(`/arecanut/report/excel?as_of=${data.as_of}`, `farmers_statement_${data.as_of}.xlsx`)}
          pdfFileName={`farmers_statement_${data.as_of}.pdf`}>
          <FarmersReportBody data={data} />
        </PrintableStatement>
      )}
    </>
  );
}

function FarmersReportBody({ data }) {
  const t = data.totals;
  return (
    <div className="font-data text-slate-800">
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
        <div>
          <h1 className="font-sans text-2xl font-semibold tracking-tight">Arecanut Farmers Statement</h1>
          <p className="text-sm text-slate-500 mt-0.5">All farmers — current position</p>
        </div>
        <div className="text-right text-xs text-slate-500">As of<br /><span className="text-slate-800">{data.as_of}</span></div>
      </div>

      <table className="w-full text-sm mb-6">
        <tbody>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Total advance</td><td className="py-1.5 text-right">{fmtMoney(t.total_advance)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Accrued interest</td><td className="py-1.5 text-right">{fmtMoney(t.total_interest)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Owed by farmers</td><td className="py-1.5 text-right">{fmtMoney(t.owed_by_farmer)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Harvest value</td><td className="py-1.5 text-right">{fmtMoney(t.total_harvest_value)}</td></tr>
          <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Net position</td><td className={`py-2 text-right font-semibold ${netLabel(t.net).cls}`}>{netLabel(t.net).text}</td></tr>
        </tbody>
      </table>

      <StatementSection title={`Farmers (${data.rows.length})`}>
        <StatementTable head={["Name", "Village", "Advance", "Interest", "Owed", "Harvest", "Net"]}
          rows={data.rows.map((r) => [
            r.name, r.village || "—", fmtMoney(r.total_advance), fmtMoney(r.total_interest),
            fmtMoney(r.owed_by_farmer), fmtMoney(r.total_harvest_value), netLabel(r.net).text,
          ])} />
      </StatementSection>

      <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
    </div>
  );
}

// Weight before any deduction -- the scale total of all bags. Older lots
// entered as a plain kg/quintal figure have no bag readings, so their
// recorded weight is the gross.
function grossKg(h) {
  return h.raw_kg != null ? h.raw_kg : h.total_kg;
}

// Final weight after bag tare and sodi -- what the lot is actually priced on.
function netKg(h) {
  return +((h.net_quintals || 0) * 100).toFixed(2);
}

const kgQ = (kg) => `${+Number(kg || 0).toFixed(2)} kg · ${+(Number(kg || 0) / 100).toFixed(4)} q`;

function buildHarvestShare(farmer, h) {
  const summary = !h.priced
    ? "*Price pending settlement*"
    : (h.value != null ? `*Value: ${fmtMoney(h.value)}*` : "*Settled in dried arecanut, no cash price*");
  return `*Arecanut Weighing Slip — ${farmer.name}*\n${h.date}\nGross weight: ${kgQ(grossKg(h))}\nNet weight: ${kgQ(netKg(h))}\n${summary}\n\n📎 Full slip attached as PDF`;
}

function HarvestSlipBody({ farmer, harvest }) {
  const bags = bagLines(harvest.bags);
  const combined = harvest.deduction_mode === "combined";
  const sodiLabel = combined ? "Tare + Sodi (wastage)" : "Sodi (wastage)";
  const showTare = !combined && harvest.tare_kg != null;
  const row = "border-b border-slate-100";
  const label = "py-1.5 text-slate-500";
  const val = "py-1.5 text-right";
  return (
    <div className="font-data text-slate-800">
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
        <div>
          <h1 className="font-sans text-2xl font-semibold tracking-tight">Arecanut Weighing Slip</h1>
          <p className="text-sm text-slate-500 mt-0.5">{farmer.name} · {farmer.village || "—"}</p>
        </div>
        <div className="text-right text-xs text-slate-500">Date<br /><span className="text-slate-800">{harvest.date}</span>{harvest.note ? <><br />{harvest.note}</> : null}</div>
      </div>

      {bags.length > 0 && (
        <table className="w-full text-xs border border-slate-300 mb-5">
          <thead>
            <tr className="bg-slate-100 text-slate-500 font-sans">
              <th className="px-3 py-2 text-left font-semibold border-r border-slate-300 w-24">Bag No</th>
              <th className="px-3 py-2 text-right font-semibold">Raw Arecanut (Kg)</th>
            </tr>
          </thead>
          <tbody>
            {bags.map((b, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-1.5 text-left border-r border-slate-200">{b.label}</td>
                <td className="px-3 py-1.5 text-right">{b.w}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 font-semibold border-t-2 border-slate-900">
              <td className="px-3 py-2 text-left border-r border-slate-300">Total{harvest.bag_count ? ` (${harvest.bag_count} bags)` : ""}</td>
              <td className="px-3 py-2 text-right">{kgQ(grossKg(harvest))}</td>
            </tr>
          </tfoot>
        </table>
      )}

      <table className="w-full text-sm">
        <tbody>
          <tr className={row}><td className={`${label} font-sans font-semibold text-slate-800`}>Gross weight</td><td className={`${val} font-semibold`}>{kgQ(grossKg(harvest))}</td></tr>
          {showTare && (
            <tr className={row}><td className={label}>Less: bag tare{harvest.bag_count ? ` (${harvest.bag_count} bags)` : ""}</td><td className={val}>− {harvest.tare_kg} kg</td></tr>
          )}
          <tr className={row}><td className={label}>Less: {sodiLabel} <span className="text-slate-400">({harvest.sodi_kg_per_quintal || 0} kg/q)</span></td><td className={val}>− {sodiDeductedKg(harvest)} kg</td></tr>
          <tr className="border-y-2 border-slate-900 bg-slate-50"><td className="py-2 font-sans font-semibold">Net weight</td><td className="py-2 text-right font-semibold">{kgQ(netKg(harvest))}</td></tr>
          {harvest.pricing_mode === "dried" ? (
            <>
              <tr className={row}><td className={label}>Conversion (kg dried/quintal)</td><td className={val}>{harvest.conversion_kg_per_quintal ?? "—"}</td></tr>
              <tr className={row}><td className={label}>Dried weight</td><td className={val}>{harvest.dried_kg != null ? `${harvest.dried_kg} kg` : "—"}</td></tr>
              {!harvest.priced ? (
                <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Value</td><td className="py-2 text-right font-semibold text-amber-600">Pending settlement</td></tr>
              ) : harvest.value != null ? (
                <>
                  <tr className={row}><td className={label}>Rate per kg (dried)</td><td className={val}>{fmtMoney(harvest.price_per_kg_dried)}</td></tr>
                  <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Value</td><td className="py-2 text-right font-semibold text-emerald-700">{fmtMoney(harvest.value)}</td></tr>
                </>
              ) : (
                <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Value</td><td className="py-2 text-right font-semibold text-slate-600">Settled in dried arecanut</td></tr>
              )}
            </>
          ) : harvest.priced ? (
            <>
              <tr className={row}><td className={label}>Rate per quintal</td><td className={val}>{fmtMoney(harvest.price_per_quintal)}</td></tr>
              <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Value</td><td className="py-2 text-right font-semibold text-emerald-700">{fmtMoney(harvest.value)}</td></tr>
            </>
          ) : (
            <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Value</td><td className="py-2 text-right font-semibold text-amber-600">Pending settlement</td></tr>
          )}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
    </div>
  );
}

function buildFarmerShare(stmt) {
  const nl = netLabel(stmt.closing_net);
  return `*Arecanut Statement — ${stmt.farmer.name}*\n${stmt.start} to ${stmt.end}\n*${nl.text}*\n\n📎 Full statement attached as PDF`;
}

function FarmerStatementBody({ stmt }) {
  const opening = netLabel(stmt.opening_net);
  const closing = netLabel(stmt.closing_net);
  return (
    <div className="font-data text-slate-800">
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
        <div>
          <h1 className="font-sans text-2xl font-semibold tracking-tight">Arecanut Statement</h1>
          <p className="text-sm text-slate-500 mt-0.5">{stmt.farmer.name} · {stmt.farmer.village || "—"}{stmt.farmer.phone ? ` · ${stmt.farmer.phone}` : ""}</p>
        </div>
        <div className="text-right text-xs text-slate-500">Period<br /><span className="text-slate-800">{stmt.start} → {stmt.end}</span></div>
      </div>

      <table className="w-full text-sm mb-6">
        <tbody>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Opening position</td><td className={`py-1.5 text-right ${opening.cls}`}>{opening.text}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Total advance (current)</td><td className="py-1.5 text-right">{fmtMoney(stmt.total_advance)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Accrued interest (current)</td><td className="py-1.5 text-right">{fmtMoney(stmt.total_interest)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Harvest value (current)</td><td className="py-1.5 text-right">{fmtMoney(stmt.total_harvest_value)}</td></tr>
          <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Closing position</td><td className={`py-2 text-right font-semibold ${closing.cls}`}>{closing.text}</td></tr>
        </tbody>
      </table>

      <StatementSection title="Advances in this period">
        <StatementTable head={["Date", "Amount", "Rate", "Interest", "Note", ""]}
          rows={stmt.advances.map((a) => [a.date, fmtMoney(a.amount), `${a.rate}%`, fmtMoney(a.accrued_interest), a.note || "—", a.settled ? "settled" : ""])} />
      </StatementSection>

      <StatementSection title="Harvest Deliveries in this period">
        <StatementTable head={["Date", "Weight (kg)", "Quintals", "Sodi (kg)", "Conv. (kg/q)", "Rate", "Value", ""]}
          rows={stmt.harvests.map((h) => [
            h.date, h.total_kg, h.quintals,
            h.sodi_kg_per_quintal ? `${sodiDeductedKg(h)} (${h.sodi_kg_per_quintal}/q)` : 0,
            h.pricing_mode === "dried" ? (h.conversion_kg_per_quintal ?? "—") : "—",
            !h.priced
              ? "pending"
              : (h.pricing_mode === "dried"
                  ? (h.price_per_kg_dried != null ? `${fmtMoney(h.price_per_kg_dried)}/kg` : "no cash price")
                  : `${fmtMoney(h.price_per_quintal)}/q`),
            !h.priced ? "pending" : (h.value != null ? fmtMoney(h.value) : "in-kind"),
            h.settled ? "settled" : "",
          ])} />
      </StatementSection>

      {stmt.settlements.length > 0 && (
        <StatementSection title="Settlements in this period">
          <StatementTable head={["Date", "Advance+Int", "Harvest", "Net", "Note"]}
            rows={stmt.settlements.map((st) => [st.date, fmtMoney(st.owed_by_farmer), fmtMoney(st.total_harvest_value), netLabel(st.net).text, st.note || "—"])} />
        </StatementSection>
      )}

      <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
    </div>
  );
}

function StatementSection({ title, children }) {
  return (
    <div className="mb-6">
      <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</h2>
      {children}
    </div>
  );
}
function StatementTable({ head, rows }) {
  return (
    <table className="w-full text-xs border border-slate-200">
      <thead>
        <tr className="bg-slate-100 text-slate-500 font-sans">
          {head.map((h, i) => <th key={i} className={`px-2 py-1.5 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-t border-slate-100">
            {r.map((c, ci) => <td key={ci} className={`px-2 py-1.5 ${ci === 0 ? "text-left" : "text-right"}`}>{c}</td>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={head.length} className="px-2 py-3 text-center text-slate-400">None</td></tr>}
      </tbody>
    </table>
  );
}

export { netLabel, StatementSection, StatementTable };
